// WikiPermsPanel.jsx — department RBAC panel. Shows the inherited (derived)
// level per pillar role and lets an admin set overrides → PUT /wiki/acl.
import React, { useEffect, useState } from 'react';
import { Shield, X, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useWikiApi } from '../../hooks/useWiki';
import { DEPT_BY_ID } from '../../lib/wikiConstants';

const LEVELS = [['admin', 'Full'], ['edit', 'Edit'], ['view', 'View'], ['none', 'None']];
const ROLE_LABEL = { admin: 'Department admin', manager: 'Manager', user: 'Member' };

export default function WikiPermsPanel({ deptId, pageRestricted, onClose }) {
    const api = useWikiApi();
    const [data, setData] = useState(null);
    const [levels, setLevels] = useState({});   // role -> level
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        let m = true;
        api.getAcl('department', deptId).then(d => {
            if (!m) return;
            setData(d);
            const init = {};
            (d.inherited || []).forEach(r => { init[r.role] = r.level || 'none'; });
            (d.overrides || []).forEach(o => { if (o.subject_type === 'role') init[o.subject_id] = o.level; });
            setLevels(init);
        }).catch(() => m && setData({ inherited: [], overrides: [] }));
        return () => { m = false; };
    }, [deptId]); // eslint-disable-line

    const inheritedFor = (role) => (data?.inherited || []).find(r => r.role === role)?.level || 'none';

    const save = async () => {
        setSaving(true);
        try {
            const entries = Object.entries(levels)
                .filter(([role, lvl]) => (lvl || 'none') !== (inheritedFor(role) || 'none'))
                .map(([role, lvl]) => ({ scope: 'department', scope_id: deptId, subject_type: 'role', subject_id: role, level: lvl }));
            await api.setAcl({ entries });
            toast.success('Permissions saved');
            onClose && onClose(true);
        } catch { toast.error('Could not save permissions'); }
        finally { setSaving(false); }
    };

    const dept = DEPT_BY_ID[deptId];

    return (
        <div className="w-[320px] border-l border-stone-200 bg-white flex flex-col h-full">
            <div className="h-12 px-4 flex items-center gap-2 border-b border-stone-200">
                <Shield className="w-4 h-4 text-stone-900" />
                <span className="text-[13px] wiki-display font-bold text-stone-900 flex-1">Permissions</span>
                <button onClick={() => onClose && onClose(false)} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-5">
                <div>
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500 mb-2">Access for {dept?.name}</div>
                    <div className="space-y-1.5">
                        {(data?.inherited || []).map(r => (
                            <div key={r.role} className="flex items-center gap-2">
                                <div className="min-w-0 flex-1"><div className="text-[12px] font-medium text-stone-900 truncate">{ROLE_LABEL[r.role] || r.role}</div>
                                    {(levels[r.role] || 'none') !== (r.level || 'none') && <div className="text-[10px] text-amber-600">overridden (was {r.level || 'none'})</div>}
                                </div>
                                <div className="flex gap-0.5 p-0.5 bg-stone-100 rounded-md">
                                    {LEVELS.map(([lv, lbl]) => (
                                        <button key={lv} onClick={() => setLevels(s => ({ ...s, [r.role]: lv }))}
                                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${(levels[r.role] || 'none') === lv ? 'bg-white wiki-shadow-soft text-stone-900' : 'text-stone-400 hover:text-stone-600'}`}>{lbl}</button>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="text-[10.5px] text-stone-400 mt-2">Defaults derive from each member’s department (pillar) role. Overrides here take precedence. Workspace admins always have full access.</div>
                </div>
                {pageRestricted && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 flex gap-2">
                        <Lock className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                        <div className="text-[12px] text-amber-900 leading-snug"><b>Page-level restriction</b> is on. Even people with department access need admin access to open this page.</div>
                    </div>
                )}
            </div>
            <div className="p-3 border-t border-stone-200">
                <button onClick={save} disabled={saving} className="w-full h-9 rounded-lg bg-stone-900 disabled:opacity-60 text-white text-[12px] font-semibold">{saving ? 'Saving…' : 'Save permissions'}</button>
            </div>
        </div>
    );
}
